import { type ReactNode } from 'react';
import Slider from 'react-slick';
import { Coin, CaretRight, CaretLeft } from '@hungryroot/icons';
import { getImageProps } from '@hungryroot/image';
import {
  IngredientsImageGrid,
  Badge,
  BadgeVariant,
  MAX_BADGES,
} from '@hungryroot/pho';

import { Heading, Paragraph, IconButton } from '@hungryroot/ramen';
import { type PairingDetail } from '@hungryroot/rest';
import {
  ProductInfo,
  type ProductInfoLabelValue,
} from '../ProductInfo/ProductInfo';
import styles from './RDP.module.css';

export interface RDPProps {
  imageUrl?: string;
  title: string;
  credits?: number;
  badges?: string[];
  badgesOverride?: ReactNode;
  warningComponent?: ReactNode;
  productInfo: ProductInfoLabelValue[];
  packagedImageUrl?: string | null;
  primaryTopIconButton?: ReactNode;
  secondaryTopIconButton?: ReactNode;
  primaryTopRightIconButton?: ReactNode;
  ingredients?: PairingDetail['ingredients'];
  brandName?: string | null;
  isEdited?: boolean;
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  onImageScroll?: (imageIndex?: number) => void;
  onProductContentScroll?: (scrollDepth: number) => void;
}

interface ReactSlickProps {
  onClick?: () => void;
  currentSlide?: number;
  slideCount?: number;
  onImageScroll?: (imageIndex?: number) => void;
}

const NextArrow = (props: ReactSlickProps) => {
  const { onClick, currentSlide, slideCount, onImageScroll } = props;

  if (currentSlide === 1 || (slideCount && slideCount < 2)) {
    return null;
  }

  return (
    <IconButton
      className={styles['next-arrow']}
      ariaLabel="Next image"
      icon={<CaretRight />}
      size="small"
      style="muted"
      prominence="primary"
      onClick={() => {
        onClick?.();
        onImageScroll?.(currentSlide);
      }}
    />
  );
};

const BackArrow = (props: ReactSlickProps) => {
  const { onClick, currentSlide, onImageScroll } = props;

  if (currentSlide === 0) {
    return null;
  }

  return (
    <IconButton
      className={styles['back-arrow']}
      ariaLabel="Previous image"
      icon={<CaretLeft />}
      size="small"
      style="muted"
      prominence="primary"
      onClick={() => {
        onClick?.();
        onImageScroll?.(currentSlide);
      }}
    />
  );
};

/**
 * This is incorrectly named. It is not specific to only Recipes but a component used for both
 * Recipes and Products
 */
export function RDP(props: RDPProps) {
  const {
    imageUrl,
    title,
    credits,
    badges,
    badgesOverride,
    warningComponent,
    productInfo,
    packagedImageUrl,
    primaryTopIconButton,
    secondaryTopIconButton,
    primaryTopRightIconButton,
    ingredients,
    isEdited,
    children,
    brandName,
    className,
    header,
    onImageScroll,
  } = props;

  const settings = {
    infinite: false,
    // setting slidesToShow to 0 or slidesToScroll to 0 will freeze the UI
    slidesToShow: 1,
    slidesToScroll: 1,
    edgeFriction: 0.1,
    speed: 150,
    touchThreshold: 50,
    arrows: true,
    dots: true,
    nextArrow: <NextArrow onImageScroll={onImageScroll} />,
    prevArrow: <BackArrow onImageScroll={onImageScroll} />,
    initialSlide: 0,
    afterChange: (current: number) => {
      onImageScroll?.(current);
    },
  };
  const image = (imageUrl: string, className?: string) => (
    <picture>
      <source
        media="only screen and (max-width: 300px)"
        {...getImageProps({
          src: imageUrl,
          width: 300,
        })}
      />
      <source
        media="only screen and (min-width: 301px) and (max-width: 600px)"
        {...getImageProps({
          src: imageUrl,
          width: 600,
        })}
      />
      {/* on desktop, we only need 520 x 350, which matches the 600 width image */}
      <source
        media="only screen and (min-width: 1025px)"
        {...getImageProps({
          src: imageUrl,
          width: 600,
        })}
      />
      <img className={className} src={imageUrl} alt="" />
    </picture>
  );

  // TODO need to change the nav bar to fixed on scroll state https://www.figma.com/file/UHhNpOxBhITAeCsrwh5fZa/PD-149-RDP%2FPDP-Redesign?node-id=1170%3A142002&mode=dev
  return (
    <div className={`${styles.container} ${className}`}>
      {header}
      {imageUrl && packagedImageUrl ? (
        <Slider {...settings}>
          {image(imageUrl)}
          <picture>
            <source
              media="only screen and (max-width: 300px)"
              {...getImageProps({
                src: packagedImageUrl,
                width: 300,
              })}
            />
            <source
              media="only screen and (min-width: 301px) and (max-width: 600px)"
              {...getImageProps({
                src: packagedImageUrl,
                width: 600,
              })}
            />
            <img
              className={styles['packaged-image']}
              src={packagedImageUrl}
              alt=""
            />
          </picture>
        </Slider>
      ) : imageUrl ? (
        image(imageUrl, styles.image)
      ) : (
        ingredients && (
          <div className={styles['image-container']}>
            <IngredientsImageGrid ingredients={ingredients} />
          </div>
        )
      )}
      {primaryTopIconButton && (
        <div className={styles['primary-top-icon-button']}>
          {primaryTopIconButton}
        </div>
      )}
      {secondaryTopIconButton && (
        <div className={styles['secondary-top-icon-button']}>
          {secondaryTopIconButton}
        </div>
      )}
      {primaryTopRightIconButton && (
        <div className={styles['primary-top-right-icon-button']}>
          {primaryTopRightIconButton}
        </div>
      )}
      <div className={styles['info-container']}>
        {warningComponent && (
          <div className={styles['warning-text-container']}>
            {warningComponent}
          </div>
        )}

        {brandName && (
          <Paragraph className={styles.brand}>{brandName}</Paragraph>
        )}
        <Heading className={styles.header} variant="H3">
          {title}
          {isEdited && (
            <Paragraph className={styles.edited}>(edited)</Paragraph>
          )}
        </Heading>
        {credits && credits > 0 ? (
          <div className={styles.credits}>
            <Coin />
            <Heading className={styles['credit-value']} variant="H6">
              {credits}
            </Heading>
          </div>
        ) : null}
        <div className={styles['product-info']}>
          <ProductInfo productInfo={productInfo} />
        </div>
        {badgesOverride
          ? badgesOverride
          : badges &&
            badges?.length > 0 && (
              <div className={styles.badges}>
                {badges.slice(0, MAX_BADGES).map((badge) => (
                  <Badge key={badge} variant={BadgeVariant.Misc}>
                    <Paragraph variant="caption">{badge}</Paragraph>
                  </Badge>
                ))}
              </div>
            )}
      </div>
      {children}
    </div>
  );
}
